import { useState } from "react";
import { numericFormatter } from "react-number-format";
import { useNavigate, useParams } from "react-router-dom";
import { useFindAllCompaniesContactsQuery, useFindAllPeopleContactsQuery } from "../../../../features/contactsApi";
import { useFindAllCustomMailsQuery, useFindEmailsHistoryQuery } from "../../../../features/mailsApi";
import { useFindOneTargetListQuery } from "../../../../features/targetlistApi";
import { ContactDTO, ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../../lib/filters/dto";
import { initialStateContactsFilters } from "../../../lib/filters/filtersSlice";
import { useAppDispatch } from "../../../lib/redux/hooks";
import { Layout } from "../../../ui/layout";
import { Spinner } from "../../../ui/molecules/spinner";
import { MenuItems } from "../../../ui/organisms/navbar/dto";
import { TabMenu } from "../../../ui/organisms/tabs/tabMenu";
import { TabPages } from "../../../ui/organisms/tabs/tabPages";
import { DeleteBulkContacts } from "../../database/commons/deleteBulk";
import { PersonDTO } from "../../database/people/dto";
import { TargetListPageEnum } from "../dto";
import { ActionsCompaniesTargetList } from "../editTargetListDetails/companies/list/actions";
import { ActionsPeopleTargetList } from "../editTargetListDetails/people/list/actions";
import {
    setTargetListPage
} from "../slice";
import { TargetCompaniesBins } from "./companies";
import { TargetPeopleBins } from "./people";

export function TargetListBins() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const targetId = useParams().targetId!;
    const tab = useParams().tab!;
    const projectId = useParams().projectId!;

    const { data: targetListData, isLoading: isLoadingTargetList } = useFindOneTargetListQuery(targetId);
    const targetList = targetListData?.targetList;
    const counters = targetListData?.counters;
    const project = targetListData?.project;

    const [peopleFilters, setPeopleFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['target-bin'][ContactType.PERSON]);
    const { data: peopleData, isLoading: isLoadingPeople } = useFindAllPeopleContactsQuery({ filters: peopleFilters, targetList: targetId, });

    const [companiesFilters, setCompaniesFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['target-bin'][ContactType.COMPANY]);
    const { data: companiesData, isLoading: isLoadingCompanies } = useFindAllCompaniesContactsQuery({ filters: companiesFilters, targetList: targetId });

    const [showDeleteBulkContactsModal, setShowDeleteBulkContactsModal] = useState<boolean>(false);
    const [showSoftBulkModal, setShowSoftBulkModal] = useState<boolean>(false);

    const [direction, setDirection] = useState<"left" | "right">("left");

    const { data: emailsHistoryData, isLoading: isLoadingEmailsHistory } = useFindEmailsHistoryQuery({ targetListId: targetId, limit: tab === 'people' ? peopleFilters.itemsPerPage * 2 : companiesFilters.itemsPerPage * 2 }, { skip: !targetId });
    const { data: emailTemplates, isLoading: isLoadingEmailTemplates } = useFindAllCustomMailsQuery({ projectId: project?.id! }, { skip: !project?.id });

    const [selectedPeopleIds, setSelectedPeopleBinsIds] = useState<{ contactId: string, personId: string }[]>([]);
    const [showPopupPeople, setShowPopupPeople] = useState<boolean>(false);
    const [selectedCompaniesIds, setSelectedCompaniesBinsIds] = useState<{ contactId: string, companyId: string }[]>([]);
    const [showPopupCompanies, setShowPopupCompanies] = useState<boolean>(false);

    if (isLoadingTargetList || isLoadingPeople || isLoadingCompanies) {
        return <div className="flex items-center justify-center h-screen w-full"><Spinner /></div>
    }

    return (
        <Layout
            headerLabel={targetList?.name + ' > Cestini'}
            headerBackPath={"/edit-target-list/" + targetId + '/' + tab + '/' + projectId}
            breadcrumbItems={[
                "Liste target",
                targetList?.name!,
                'Cestini',
                (tab === 'people' ? 'Persone' : 'Aziende')
            ]}
            menuItem={MenuItems.TARGET_LIST}
            headerChildren={<div className="flex items-center justify-between">
                <TabMenu
                    direction={direction}
                    setDirection={(value) => setDirection(value)}
                    setValue={(e) => {
                        if (e === 0) {
                            navigate(window.location.pathname.replace(/\/people|\/companies/, "/people"));
                        }
                        if (e === 1) {
                            navigate(window.location.pathname.replace(/\/specifics|\/people/, "/companies"));
                        }
                        dispatch(setTargetListPage(TargetListPageEnum.LIST));
                    }}
                    value={tab === 'people' ? 0 : 1}
                    items={[
                        {
                            icon: <></>,
                            label:
                                "Persone (" +
                                numericFormatter(counters?.find(count => count.contactType === ContactType.PERSON)?.dto.targetListBin.total.toString() ?? "0", { thousandSeparator: '.' })
                                +
                                ")",
                        },
                        {
                            icon: <></>,
                            label:
                                "Aziende (" +
                                numericFormatter(counters?.find(count => count.contactType === ContactType.COMPANY)?.dto.targetListBin.total.toString() ?? "0", { thousandSeparator: '.' })
                                +
                                ")",
                        },
                    ]}
                />
                <div>
                    {
                        tab === 'people' &&
                        <ActionsPeopleTargetList
                            isInBin
                            setFilters={setPeopleFilters}
                            setSelectedIds={setSelectedPeopleBinsIds}
                            setShowSoftBulkModal={setShowSoftBulkModal}
                            setDeleteBulkContactsModal={setShowDeleteBulkContactsModal}
                            targetList={targetList!}
                            contacts={peopleData as unknown as FindContactsTargetResponse<ContactType.PERSON>}
                            selectedIds={selectedPeopleIds} setShowPopup={(action) => setShowPopupPeople(action)} filters={peopleFilters} />
                    }
                    {
                        tab === 'companies' &&
                        <ActionsCompaniesTargetList
                            isInBin
                            setFilters={setCompaniesFilters}
                            setSelectedIds={setSelectedCompaniesBinsIds}
                            setShowSoftBulkModal={setShowSoftBulkModal}
                            setDeleteBulkContactsModal={setShowDeleteBulkContactsModal}
                            targetList={targetList!}
                            filters={companiesFilters} selectedIds={selectedCompaniesIds} setShowPopup={(action) => setShowPopupCompanies(action)}
                            contacts={companiesData as unknown as {
                                total: number
                                page: number
                                perPage: number
                                prev: boolean
                                next: boolean
                                totalPage: number
                                data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
                            }} />
                    }
                </div>
            </div>}
        >
            <div className="h-full max-h-full p-4">
                <div className="flex flex-col bg-white rounded-2xl shadow-sm h-full max-h-full">
                    <div style={{ height: 'calc(100%)' }} className="border-t border-t-neutral-200">
                        <TabPages
                            direction={direction}
                            setDirection={(value) => setDirection(value)}
                            pages={[
                                <div key={0} className="h-full">
                                    <TargetPeopleBins
                                        templates={emailTemplates!}
                                        isLoadingEmailsHistory={isLoadingEmailsHistory}
                                        emailsHistory={emailsHistoryData!}
                                        project={project!}
                                        targetList={targetList!}
                                        counters={counters?.find(count => count.contactType === ContactType.PERSON)!}
                                        toActivate={0}
                                        contacts={peopleData as unknown as FindContactsTargetResponse<ContactType.PERSON>}
                                        selectedIds={selectedPeopleIds}
                                        setSelectedIds={arg => setSelectedPeopleBinsIds(arg)}
                                        setShowPopup={arg => setShowPopupPeople(arg)}
                                        showPopup={showPopupPeople}
                                        filters={peopleFilters}
                                        setPage={action => setPeopleFilters((prev) => ({ ...prev, page: action }))}
                                        setFilters={action => setPeopleFilters((prev) => ({ ...prev, ...action }))}
                                        resetFilters={() => setPeopleFilters(initialStateContactsFilters['target-bin'][ContactType.PERSON])}
                                    />
                                </div>,
                                <div key={1} className="h-full">
                                    <TargetCompaniesBins
                                        templates={emailTemplates!}
                                        isLoadingEmailsHistory={isLoadingEmailsHistory}
                                        emailsHistory={emailsHistoryData!}
                                        project={project!}
                                        targetList={targetList!}
                                        counters={counters?.find(count => count.contactType === ContactType.COMPANY)!}
                                        toActivate={0}
                                        contacts={companiesData as unknown as {
                                            total: number
                                            page: number
                                            perPage: number
                                            prev: boolean
                                            next: boolean
                                            totalPage: number
                                            data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
                                        }}
                                        selectedIds={selectedCompaniesIds}
                                        setSelectedIds={arg => setSelectedCompaniesBinsIds(arg)}
                                        setShowPopup={arg => setShowPopupCompanies(arg)}
                                        showPopup={showPopupCompanies}
                                        filters={companiesFilters}
                                        setPage={action => setCompaniesFilters((prev) => ({ ...prev, page: action }))}
                                        setFilters={action => setCompaniesFilters((prev) => ({ ...prev, ...action }))}
                                        resetFilters={() => setCompaniesFilters(initialStateContactsFilters['target-bin'][ContactType.COMPANY])}
                                    />
                                </div>,
                            ]}
                            value={tab === 'people' ? 0 : 1}
                            setValue={(e) => {
                                if (e === 0) {
                                    navigate(window.location.pathname.replace(/\/people|\/companies/, "/people"));
                                }
                                if (e === 1) {
                                    navigate(window.location.pathname.replace(/\/specifics|\/people/, "/companies"));
                                }
                                // dispatch(setSelectedContact(undefined))
                                dispatch(setTargetListPage(TargetListPageEnum.LIST));
                            }}
                        />
                    </div>
                </div>
            </div>
            <DeleteBulkContacts
                filters={tab === 'people' ? peopleFilters : companiesFilters}
                setFilters={tab === 'people' ? setPeopleFilters : setCompaniesFilters}
                showModal={showDeleteBulkContactsModal}
                setShowModal={setShowDeleteBulkContactsModal}
                contactType={tab === 'people' ? ContactType.PERSON : ContactType.COMPANY}
                section={"target-bin"}
                targetId={targetId}
            />
        </Layout>
    );
}
